import React, { PropsWithChildren, createContext, useCallback, useEffect, useState } from 'react';
import defaults from 'lodash/defaults';

import createContextHelpers from 'utils/create-context-helpers';

type ViewportDimensions = {
  height: number;
  width: number;
  offset: number;
  offsetTop: number;
  parentOffset: number;
  fixedHeaderHeight?: number;
  fixedFooterHeight?: number;
  stickyHeaderRolloutEnabled?: boolean;
};

type SetViewport = (
  viewport: Partial<ViewportDimensions> & { trustedParentViewport?: ViewportDimensions | null }
) => void;

type ViewportState = ViewportDimensions & {
  trustedParentViewport?: ViewportDimensions | null;
  hasEmbeddedHostViewport: boolean;
  setViewport: SetViewport;
};

const noopSetViewport: SetViewport = () => undefined;

// Preserve the original context's `undefined` runtime default; real consumers are all
// mounted beneath ViewportProvider and were already written under that invariant.
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const ViewportContext = createContext<ViewportState>(undefined!);
export const { useViewport, withViewport } = createContextHelpers(ViewportContext, 'viewport');

export const ViewportProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [viewportState, setViewportState] = useState<ViewportState>({
    height: 0,
    width: 0,
    offset: 0,
    offsetTop: 0,
    parentOffset: 0,
    fixedHeaderHeight: 0,
    fixedFooterHeight: 0,
    stickyHeaderRolloutEnabled: false,
    trustedParentViewport: null,
    hasEmbeddedHostViewport: false,
    // Replaced with the real callback below before this value reaches Provider consumers.
    // Keeping the key in the initial state gives the context one honest, stable shape without
    // asserting that a partial state is complete.
    setViewport: noopSetViewport,
  });
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setViewportState((currentViewport) => ({
      ...currentViewport,
      width: window.innerWidth,
      height: window.innerHeight,
    }));
  }, []);

  useEffect(() => {
    if (disabled) {
      return undefined;
    }

    const handleWindowResize = (): void => {
      setViewportState((currentViewport) => ({
        ...currentViewport,
        width: window.innerWidth,
        height: window.innerHeight,
      }));
    };
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [disabled]);

  const setViewport = useCallback<SetViewport>(
    (viewport) => {
      const hasHostWidth = typeof viewport.width === 'number' && viewport.width > 0;
      if (!disabled) {
        setDisabled(true);
      }

      setViewportState((currentViewport) => ({
        ...defaults({ ...viewport }, currentViewport),
        hasEmbeddedHostViewport: currentViewport.hasEmbeddedHostViewport || hasHostWidth,
      }));
    },
    [disabled]
  );

  viewportState.setViewport = setViewport;

  return <ViewportContext.Provider value={viewportState}>{children}</ViewportContext.Provider>;
};
